import React from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { CLIENT_TYPE, HTTP_BAD_REQUEST } from "../../common/api";
import { useTranslation, Trans } from "react-i18next";
import { useLoadingIndicator } from "../../common/context/LoadingIndicatorContext";
import {
  Account,
  AccountContext,
  RetryBehavior,
} from "../../common/context/AccountContext";
import {
  AnalyticsContext,
  ViewSignUpEvent,
  LoginWithClassLinkButtonTapEvent,
  LoginWithCleverButtonTapEvent,
} from "../../common/context/AnalyticsContext";
import {
  AppFlowStepContainer,
  AppFlowTitle,
  AppFlowStepContentContainer,
  AppFlowContinueButton,
  AppFlowModal,
  SchoolLoginButton,
  SchoolLoginButtonIconImage,
} from "../../common/CommonStyles";
import { EMAIL_ADDRESS_REGEX } from "../../common/utils";
import { COLOR_TEXT_PRIMARY, COLOR_GRAY_DISABLED, COLOR_SURFACE_CONTAINER_ALPHA } from "../../common/colors";

const FormElement = styled.div`
  width: 300px;
`;

const enum State {
  Input = 1,
  InProgress = 2,
  CredentialsError = 3,
  NetworkError = 4,
  EmailError = 5,
  EmptyError = 6,
}

export const SignUp: React.FC<{
  account?: Account;
  next: () => void;
}> = ({ account, next }) => {
  const { t } = useTranslation();
  const { logEvent } = React.useContext(AnalyticsContext);
  const { makeRequest } = React.useContext(AccountContext);
  const { login, updateUser, loginWithClever, loginWithClassLink } =
    React.useContext(AccountContext);
  const { startLoading, stopLoading } = useLoadingIndicator(false);

  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [canContinue, setCanContinue] = React.useState(false);

  const [state, setState] = React.useState(State.Input);
  const [customErrorMessage, setCustomErrorMessage] = React.useState("");
  const [showErrorModal, setShowErrorModal] = React.useState(false);

  const openErrorModal = () => setShowErrorModal(true);
  const closeErrorModal = () => {
    setShowErrorModal(false);
    setState(State.Input);
  };

  const shouldLogViewSignUp = React.useRef<boolean>(true);

  if (shouldLogViewSignUp.current) {
    logEvent(new ViewSignUpEvent());
    shouldLogViewSignUp.current = false;
  }

  React.useEffect(() => {
    if (account?.user?.has_unconfirmed_email === true) {
      next();
    }
  }, [account, next]);

  React.useEffect(() => {
    const errorStates = [
      State.CredentialsError,
      State.NetworkError,
      State.EmailError,
      State.EmptyError,
    ];
    if (errorStates.includes(state)) {
      openErrorModal();
    }
  }, [state]);

  React.useEffect(() => {
    if (email.trim() !== "" && password.trim() !== "") {
      setCanContinue(true);
    }
  }, [email, password]);

  const signUp = React.useCallback(async () => {
    if (state === State.InProgress) {
      // Intentionally left empty for double clicks
    } else if (email === "" || password === "") {
      setState(State.EmptyError);
    } else if (!email.match(EMAIL_ADDRESS_REGEX)) {
      setState(State.EmailError);
    } else {
      // Consider adding error states if the form doesn't handle them automatically
      startLoading();
      setState(State.InProgress);
      const unconfirmedLoginDetails = {
        unconfirmed_email: email,
        password1: password,
        password2: password,
      };
      if (account?.user?.is_guest === true) {
        const signUpResponse = await makeRequest(
          "auth/registration/unconfirmed-login-details/",
          "POST",
          RetryBehavior.RETRY_EXCEPTION_EXPOSE_NON_SUCCESS,
          true,
          unconfirmedLoginDetails
        );
        const user = signUpResponse.body;
        if (user === null || user === undefined) {
          if (signUpResponse.code === HTTP_BAD_REQUEST) {
            let message = "";
            const errorBody = signUpResponse.errorBody;

            if (errorBody) {
              message = message || errorBody.email;
              message = message || errorBody.unconfirmed_email;
              message = message || errorBody.password1;
            }
            if (Array.isArray(message)) {
              message = message.join("\n");
            }

            setCustomErrorMessage(message);
            setState(State.CredentialsError);
          } else {
            setState(State.NetworkError);
          }
        } else {
          updateUser(user, true, null, false);
        }
      } else {
        unconfirmedLoginDetails["client_type"] = CLIENT_TYPE;
        const signUpResponse = await makeRequest(
          "auth/registration/guest-user/",
          "POST",
          RetryBehavior.RETRY_EXCEPTION_EXPOSE_NON_SUCCESS,
          false,
          { unconfirmed_login_details: unconfirmedLoginDetails }
        );
        if (signUpResponse.body === null || signUpResponse.body === undefined) {
          if (signUpResponse.code === HTTP_BAD_REQUEST) {
            let message = "";
            const errorBody = signUpResponse.errorBody;

            if (errorBody) {
              message = message || errorBody.unconfirmed_login_details?.email;
              message =
                message || errorBody.unconfirmed_login_details?.unconfirmed_email;
              message = message || errorBody.unconfirmed_login_details?.password1;
            }
            if (Array.isArray(message)) {
              message = message.join("\n");
            }

            setCustomErrorMessage(message);
            setState(State.CredentialsError);
          } else {
            setState(State.NetworkError);
          }
        } else {
          login(signUpResponse.body.access_token, signUpResponse.body.user);
        }
      }
      stopLoading();
    }
  }, [
    makeRequest,
    startLoading,
    stopLoading,
    state,
    setState,
    email,
    password,
    login,
    updateUser,
    account,
  ]);

  return (
    <AppFlowStepContainer>
      <AppFlowTitle
        title={t("signup_title_text")}
        subtitle={
          <Trans
            t={t}
            i18nKey="signup_subtitle_text"
            components={{
              linkText: (
                <Link className="text-decoration-none" to="/account/login/" />
              ),
            }}
          />
        }
      />
      <AppFlowStepContentContainer>
        <AppFlowModal
          title={t("error_alert_title")}
          body={
            <div style={{ whiteSpace: "pre-line" }}>
              {customErrorMessage
                ? customErrorMessage
                : state === State.CredentialsError
                  ? t("signup_default_error")
                  : state === State.NetworkError
                    ? t("signup_network_error")
                    : state === State.EmptyError
                      ? t("login_network_empty_error")
                      : state === State.EmailError
                        ? t("login_network_email_error")
                        : t("error_alert_title")}
            </div>
          }
          closeButtonText={t("generic_ok")}
          shouldShowModal={showErrorModal}
          closeModal={closeErrorModal}
        />
        <div className="d-flex align-items-center justify-content-center gap-3 flex-column">
          <FormElement className="form-floating">
            <input
              type="email"
              className="form-control"
              id="email"
              required={true}
              placeholder=""
              onChange={(e) => setEmail(e.target.value)}
              style={{ backgroundColor: COLOR_SURFACE_CONTAINER_ALPHA }}
            />
            <label className="text-muted" id="email-label" htmlFor="email">
              {t("signup_email_hint")}
            </label>
          </FormElement>
          <FormElement className="form-floating">
            <input
              type="password"
              className="form-control"
              id="password"
              required={true}
              placeholder=""
              onChange={(e) => setPassword(e.target.value)}
              style={{ backgroundColor: COLOR_SURFACE_CONTAINER_ALPHA }}
            />
            <label
              className="text-muted"
              id="password-label"
              htmlFor="password"
            >
              {t("signup_password_hint")}
            </label>
          </FormElement>
          <AppFlowContinueButton
            text={t("signup_button_text")}
            enabled={canContinue}
            onClick={() => signUp()}
          />
        </div>
        <div className="text-center">
          <hr
            style={{
              marginTop: 20,
              marginLeft: "auto",
              marginRight: "auto",
              marginBottom: 10,
              width: 300,
              color: COLOR_TEXT_PRIMARY,
            }}
          />
          <p style={{ color: COLOR_GRAY_DISABLED }}>{t("login_or")}</p>
          <SchoolLoginButton
            disabled={false}
            onClick={() => {
              logEvent(new LoginWithCleverButtonTapEvent());
              loginWithClever();
            }}
          >
            <SchoolLoginButtonIconImage
              src={`${process.env.PUBLIC_URL}/clever.png`}
              fluid
            />
            {t("login_with_provider", { provider: "Clever" })}
          </SchoolLoginButton>
          <SchoolLoginButton
            disabled={false}
            onClick={() => {
              logEvent(new LoginWithClassLinkButtonTapEvent());
              loginWithClassLink();
            }}
          >
            <SchoolLoginButtonIconImage
              src={`${process.env.PUBLIC_URL}/classlink.png`}
              fluid
            />
            {t("login_with_provider", { provider: "ClassLink" })}
          </SchoolLoginButton>
        </div>
      </AppFlowStepContentContainer>
    </AppFlowStepContainer>
  );
};
